import type { ContentUser } from "@amzn/ads-discovery-theme-service-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";
import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";
import { useStore } from "zustand";

import { useMarketingNarrativeFilters } from "../../../../hooks/marketingContent/useMarketingNarrativeFilters";
import { useMarketingNarrativesLoader } from "../../../../hooks/marketingContent/useMarketingNarrativesLoader";
import { discoveryFilterStore } from "../../../../store/filters";
import { navigationStore } from "../../../../store/navigation";
import { salesforceStore } from "../../../../store/salesforce";
import { IHubContentProvider } from "../../../../types/providers";
import { isSuccess, parseSharableLinkId } from "../../../../utils/api.utils";
import { MarketingAccessError } from "../../../error/MarketingAccessError";
import { UnauthorizedBoundary } from "../../errorBoundary/UnauthorizedBoundary";
import { withInsightsRenderer } from "../withInsightsRenderer";

import { MarketingNarrativeCard } from "./MarketingNarrativeCard";

interface MarketingNarrativesViewerProps {
    isLoaderInView: boolean;
    setAsyncStatus: Dispatch<SetStateAction<AsyncStatus>>;
    setTotalCount: Dispatch<SetStateAction<number>>;
}

const MarketingNarrativesContent: React.FC<MarketingNarrativesViewerProps> = (
    props,
) => {
    const { isLoaderInView, setAsyncStatus, setTotalCount } = props;

    const rawIdentifier = useStore(
        navigationStore,
        (state) => state.identifier,
    );
    const marketingContentId = rawIdentifier
        ? parseSharableLinkId(rawIdentifier).identifier
        : undefined;

    const [asyncStatus, lazyLoadedContents, totalResults] =
        useMarketingNarrativesLoader({
            isLoaderInView,
            marketingContentId,
        });

    // Mock contentUsers data - replace with actual data when available
    const contentUsers: ContentUser[] = [
        {
            userId: "vermautk",
            permissionType: "OWNER",
        },
    ];

    const {
        applyDefaultMarketingNarrativeFilters,
        applyMarketingNarrativesCascading,
    } = useMarketingNarrativeFilters();
    const [sfAsyncStatus] = useStore(salesforceStore, (state) => [
        state.asyncStatus,
    ]);
    const [fieldSelections] = useStore(discoveryFilterStore, (state) => [
        state.MARKETING_CONTENT.filters[
            IHubContentProvider.MARKETING_NARRATIVES
        ].fieldSelections,
    ]);

    useEffect(() => {
        setAsyncStatus(asyncStatus);
    }, [asyncStatus]);

    useEffect(() => {
        if (isSuccess(asyncStatus)) {
            setTotalCount(totalResults);
        }
    }, [asyncStatus, totalResults]);

    useEffect(() => {
        if (isSuccess(sfAsyncStatus)) {
            applyDefaultMarketingNarrativeFilters();
        }
    }, [sfAsyncStatus]);

    useEffect(() => {
        applyMarketingNarrativesCascading();
    }, [fieldSelections]);

    return (
        <>
            {lazyLoadedContents.length > 0 &&
                lazyLoadedContents.map((content, index) => (
                    <MarketingNarrativeCard
                        content={content}
                        contentUser={contentUsers}
                        key={content.contentId}
                        index={index}
                    />
                ))}
            {asyncStatus === "error" && <div>Failed to load contents</div>}
        </>
    );
};

const WrappedMarketingNarratives = withInsightsRenderer(
    MarketingNarrativesContent,
    { showSearchSortDropdown: true },
);

export const MarketingNarrativesViewer: React.FC = () => {
    const [, , , isUnauthorized] = useMarketingNarrativesLoader({});

    return (
        <UnauthorizedBoundary
            isUnauthorized={isUnauthorized}
            ErrorComponent={MarketingAccessError}
        >
            <WrappedMarketingNarratives />
        </UnauthorizedBoundary>
    );
};
